import { MouseEvent, useEffect, useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { ButtonProps, SelectProps, Spin } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { debounce } from 'lodash';

import { useUserAuth } from '../auth';
import { useWorkerQuery } from '../azure';

type UseWorkerPickerOptions = {
  excludeSelf?: boolean;
  labelInValue?: boolean;
  mode?: SelectProps['mode'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue?: (newValue: any) => void;
};

export const useWorkerPicker = (config?: UseWorkerPickerOptions) => {
  const [search, setSearch] = useState('');
  const { data, isFetching, isPending } = useWorkerQuery(search);
  const { user } = useUserAuth();

  useEffect(() => {
    if ((config?.mode === 'multiple') !== Array.isArray(config?.value))
      console.error('WorkerPicker: Data type of value does not match the selection mode.');
  }, [config?.mode, config?.value]);

  const options = data
    ?.filter((x) => !!x.jobTitle && (!config?.excludeSelf || x.jobTitle !== user?.enterpriseId))
    .map((x) => ({ value: x.jobTitle, label: x.displayName }));

  const getSelectProps = (
    props?: Omit<
      SelectProps,
      'labelInValue' | 'loading' | 'mode' | 'notFoundContent' | 'options' | 'showSearch' | 'onSearch'
    >,
  ) => ({
    style: { minWidth: 600 },
    allowClear: true,
    filterOption: false,
    labelInValue: config?.labelInValue ?? true,
    loading: isFetching || isPending,
    mode: config?.mode,
    notFoundContent: isFetching || isPending ? <Spin size="small" /> : null,
    options,
    showSearch: true,
    onSearch: debounce(setSearch, 300),
    ...props,
  });

  const getAssignSelfButtonProps = (props?: ButtonProps) => ({
    ...props,
    children: config?.mode === 'multiple' ? 'Add Me' : 'Assign to Me',
    icon: <UserOutlined />,
    onClick: (e: MouseEvent<HTMLDivElement>) => {
      const me = config?.labelInValue
        ? options?.find((x) => x.value === user?.enterpriseId)
        : options?.find((x) => x.value === user?.enterpriseId)?.value;

      if (config?.mode === 'multiple') {
        if (!config?.value.find((x: DefaultOptionType) => x.value === user?.enterpriseId) && me) {
          config?.setValue && config.setValue([...config.value, me]);
        }
      } else if (me) {
        config?.setValue && config.setValue(me);
      }

      props?.onClick && props?.onClick(e);
    },
  });

  return { getAssignSelfButtonProps, getSelectProps };
};
